import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import tw from 'twin.macro';
import { LOCATION_ICON } from 'assets/icons/icons';
import { getSubjectIcon } from 'assets/icons/subjects/subject-icons';
import { CustomIcon } from 'components/custom-icon/CustomIcon';
import { GroupWrap } from 'components/group-wrap/group-wrap';
import { Stack } from 'components/stack/stack';
import { SubjectIcon } from 'components/subject-icon/SubjectIcon';
import { Text } from 'components/text/Text';
import { OfferResponse, OpeningHoursResponse } from 'logic/api-models/api-models';
import { mapCurrency } from 'logic/currency/map-currency';
import { getSubjectName, getSubjectNameWithSoftHyphens } from 'logic/store/subject/subject.logic';
import { getSubjectsByNumber } from 'logic/store/subject/subject.selectors';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { SubjectType } from 'models/subject-type';
import { StarsRating } from 'components/rating/StarsRating';
import { useTutorInfo } from '../useTutorInfo';
import { RatingsModal } from 'components/rating/ratings.modal';
import { modalSlice } from 'logic/store/modal/modal.slice';
import { RatingBadgesGroup } from 'components/rating/RatingBadgesGroup';
import { RatingBadge } from 'components/rating/RatingBadge';
import { BadgesNames } from 'models/badges';
import { useBreakpoints } from 'logic/hooks/use-breakpoints';
import getIsTutoringSchool from 'logic/helpers/isTutoringSchool';
import { getWeekDaySelectOptions } from 'logic/api-models/week-day.logic';
import { getTimeOfDaySelectOptions } from 'logic/api-models/time-of-day.logic';
import { mapOpeningHoursToDisplay } from 'logic/store/institute/institute.logic';
import { colors } from 'src/styles/colors';
import { getLinkSrc } from 'logic/utils/link';
import { isEmpty } from 'lodash';
import { featureFlags } from 'logic/feature-flag/feature-flag';
import { formatAverageRating } from 'logic/utils/number';
import { isElitetutor, isRefugeeTutor } from 'logic/helpers/tutorHelper';
import { useLocation } from 'react-use';
import { PixelEvents, triggerFBPixelEvent } from 'logic/analytics/analytics';
import { useLocalizedI18nGlobal } from '../../../../logic/translations/use-localized-i18n-global';

const IconWrapper = styled.div`
  ${tw`flex justify-start items-center mt-1`};
  height: 16px;
  max-height: 16px;
  img {
    height: 100%;
    width: 11px;
    object-fit: contain;
  }
`;

const Hours = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  width: 100%;
`;

const DayLabel = styled.div<any>`
  min-width: 35px;
  margin-right: ${({ minTablet }) => (minTablet ? '50px' : '20px')};
`;

const Day = styled.div<any>`
  padding: ${({ minTablet }) => (minTablet ? '15px 40px' : '15px')};
  max-height: 52px;
  overflow: hidden;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: ${colors.gray12};
  margin-bottom: 8px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const HoursWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const BadgesWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 12px 0;
  overflow-x: auto;
`;

const RatingWrapper = styled(GroupWrap)`
  width: 100%;
`;

export const TutorInfo: React.FC<{
  offer: OfferResponse | undefined;
}> = ({ offer, children }) => {
  useLocalizedI18nGlobal();
  const subjectsByNumber = useSelector(getSubjectsByNumber);
  const {
    age,
    tutorNameLabel,
    isMyOffer,
    badges,
    averageRating,
    totalRatings,
    city,
  } = useTutorInfo({
    offer,
  });
  const isTutoringSchool = getIsTutoringSchool(offer);
  const location = useLocation();
  const _isElitetutor = isElitetutor(offer, location.pathname);
  const _isRefugeeTutor = isRefugeeTutor(offer, location.pathname);
  const dispatch = useDispatch();
  const { min400, minTablet } = useBreakpoints();

  useEffect(() => {
    triggerFBPixelEvent(PixelEvents.CustomizeProduct);
  }, []);

  const renderMyBadgesIfCan = () => {
    const hasAnyBadge = Object.values(badges).some((count) => count || 0 > 0);

    return (
      hasAnyBadge && (
        <BadgesWrapper>
          <RatingBadgesGroup inline={!min400}>
            {Object.entries(badges)
              .map(
                ([badge, count]) =>
                  count && <RatingBadge badge={badge as BadgesNames} count={count} />
              )
              .filter(Boolean)}
          </RatingBadgesGroup>
        </BadgesWrapper>
      )
    );
  };

  const renderMyRatingsIfCan = () =>
    totalRatings > 0 &&
    isMyOffer &&
    !isTutoringSchool && (
      <Stack textAlign={'center'} flow={'row'} gap={1}>
        <Text size={14} fontWeight={500}>
          {translate(translationKeys.tutorDetails.info.reviews)} ({totalRatings})
        </Text>
        <Text size={14} color={'primary'} fontWeight={600}>
          {formatAverageRating(averageRating)}/5
        </Text>
        <StarsRating value={averageRating} allowChange={false} size={14} spaceStars={4} />
        {renderMyBadgesIfCan()}
        <Text
          color={'primary'}
          underline
          style={{ cursor: 'pointer' }}
          onClick={() => dispatch(modalSlice.actions.openReviewsModal())}
        >
          {translate(translationKeys.ratings.showMyRatingsCTA)}
        </Text>
      </Stack>
    );

  const renderHours = () => {
    const pickedHours = mapOpeningHoursToDisplay(
      offer?.tutoringSchool?.openingHours as (OpeningHoursResponse | undefined)[] | undefined
    );

    if (isEmpty(pickedHours)) {
      return (
        <div>
          <Text
            color="primary"
            size={14}
            strong
            style={{ marginBottom: '0.5rem', display: 'block' }}
          >
            {translate(translationKeys.offerDetails.hours)}
          </Text>
          <HoursWrap>
            <Day minTablet={minTablet}>
              <Hours
                style={{
                  justifyContent: 'center',
                }}
              >
                <Text color="lightGray" fontWeight={600} style={{ textTransform: 'uppercase' }}>
                  {translate(translationKeys.offerDetails.notProvided)}
                </Text>
              </Hours>
            </Day>
          </HoursWrap>
        </div>
      );
    }
    if (pickedHours)
      return (
        <div>
          <Text
            color="primary"
            size={14}
            strong
            style={{ marginBottom: '0.5rem', display: 'block' }}
          >
            {translate(translationKeys.offerDetails.hours)}
          </Text>
          <HoursWrap>
            {getWeekDaySelectOptions().map((weekDay, dayIndex) => (
              <Day minTablet={minTablet} key={dayIndex}>
                <DayLabel minTablet={minTablet}>
                  <Text color="lightGray" fontWeight={600} style={{ textTransform: 'uppercase' }}>
                    {weekDay.label}
                  </Text>
                </DayLabel>
                <Hours>
                  {pickedHours[weekDay.value] !== undefined ? (
                    getTimeOfDaySelectOptions().map((timeOfDay, timeOfDayIndex) => (
                      <Text key={timeOfDayIndex} fontWeight={600}>
                        {pickedHours[weekDay.value]?.[timeOfDay.value]?.join(' - ')}
                      </Text>
                    ))
                  ) : (
                    <Text fontWeight={600}>
                      {translate(translationKeys.common.openingHours.closed)}
                    </Text>
                  )}
                </Hours>
              </Day>
            ))}
          </HoursWrap>
        </div>
      );
  };

  const renderTutorStarsIfCan = () =>
    totalRatings > 0 && (
      <RatingWrapper gap={0.5}>
        <StarsRating value={averageRating} allowChange={false} size={15} spaceStars={4} />
        <Text size={15} color={'primary'} strong>
          {formatAverageRating(averageRating)}
        </Text>
        <Text size={15} color={'primary'} strong>
          ({totalRatings} {translate(translationKeys.tutorDetails.info.reviews)})
        </Text>
      </RatingWrapper>
    );

  return (
    <>
      <Stack flow="row">
        <Text color="grey" size={25} strong>
          {isTutoringSchool ? `${tutorNameLabel} ${city}` : tutorNameLabel}
        </Text>
        {!isTutoringSchool && (
          <Text type="secondary" size={25}>
            {age} {translate(translationKeys.tutorDetails.info.age)}
          </Text>
        )}
        {_isRefugeeTutor ? (
          <>
            <Text color="primary" size={25} strong>
              {translate(translationKeys.common.voluntary)}
            </Text>
          </>
        ) : _isElitetutor ? (
          <>
            <Text color="grey" size={20} strong>
              {translate(translationKeys.tutorDetails.info.elitetutor)}
            </Text>
            <Text color="grey" size={14} strong>
              {translate(translationKeys.tutorDetails.info.elitetutorExtraInfo)}
            </Text>
          </>
        ) : null}
        {isTutoringSchool && renderTutorStarsIfCan()}
        {!_isElitetutor && !_isRefugeeTutor && (
          <Text color="primary" size={25} strong>
            {isTutoringSchool
              ? translate(translationKeys.common.costSchool, {
                  value: offer?.priceAtleast || '',
                  currency: mapCurrency(offer?.priceCurrency) || '',
                })
              : translate(translationKeys.common.costAndRate, {
                  value: offer?.priceAtleast || '',
                  currency: mapCurrency(offer?.priceCurrency) || '',
                  time: offer?.tutoringTime || '',
                })}
          </Text>
        )}
        {offer?.location && !_isElitetutor && (
          <IconWrapper>
            <Text color="grey" size={15} strong>
              <CustomIcon height={10} className="policy__image" src={LOCATION_ICON} />{' '}
              {` ${offer?.location?.place}`}
            </Text>
          </IconWrapper>
        )}

        {children}

        {offer?.tutoringSchool?.impressumUrl && (
          <a
            href={getLinkSrc(offer?.tutoringSchool?.impressumUrl)}
            target="_blank"
            rel="noreferrer"
          >
            <Text
              color="lightGray"
              size={12}
              style={{ textDecoration: 'underline', marginBottom: 5, display: 'block' }}
            >
              {translate(translationKeys.tutorDetails.info.impressum)}
            </Text>
          </a>
        )}
        <Text color="primary" size={14} strong>
          {translate(translationKeys.tutorDetails.info.subjectsOffered)}
        </Text>
      </Stack>

      <GroupWrap gap={0}>
        {!_isElitetutor &&
          offer?.subjects?.map((subject, index) => {
            const subjectType = (subject as unknown) as SubjectType;
            const subjectIcon = getSubjectIcon(subjectType);
            return (
              <div key={index}>
                <SubjectIcon
                  icon={subjectIcon}
                  text={getSubjectNameWithSoftHyphens(subjectsByNumber, subjectType)}
                  isTextSafeAsHTML
                />
              </div>
            );
          })}
        {_isElitetutor &&
          offer?.elitetutor?.approvedSubjects?.map((subject, index) => {
            const subjectType = (subject as unknown) as SubjectType;
            const subjectIcon = getSubjectIcon(subjectType);
            return (
              <div key={index}>
                <SubjectIcon
                  icon={subjectIcon}
                  text={getSubjectNameWithSoftHyphens(subjectsByNumber, subjectType)}
                  isTextSafeAsHTML
                />
              </div>
            );
          })}
      </GroupWrap>

      {isTutoringSchool && renderHours()}
      {featureFlags.ratings && renderMyRatingsIfCan()}
      {featureFlags.ratings && <RatingsModal />}
    </>
  );
};
